import React, { useCallback, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { tA11y } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import TabView from './TabView';

type Props = {
  tabs: Array<{
    Component?: React.FC<{
      isSelected?: boolean;
      handleOnClick?: (route: string | undefined) => void;
    }>;
    label?: string;
    // Icon?: React.ReactElement;
    Icon?: any;
  }>;
  tabViews: Array<{
    Component: React.FC<{ singleLetter?: string; fromRoute?: string }>;
  }>;
  defaultTabIndex: number;
  handleStateChange?: boolean;
  isElevated?: boolean;
  handleOnClick?: (route: string | undefined) => void;
};

const TabBar: React.FC<Props> = ({
  tabs,
  tabViews,
  defaultTabIndex = 0,
  handleStateChange = false,
  isElevated = true,
  handleOnClick,
}) => {
  const classes = useStyles({ tabsLength: tabs.length });
  const [value, setValue] = React.useState(defaultTabIndex);
  const [singleLetter] = useParams()?.letter ?? [''];
  const location = useLocation();

  const handleChange = useCallback((event: React.ChangeEvent<unknown>, newValue: number) => {
    if (!handleStateChange) {
      setValue(newValue);
    }
  }, []);

  useEffect(() => {
    setValue(() => defaultTabIndex);
  }, [defaultTabIndex]);

  return (
    <div>
      <AppBar
        id="tab-bar"
        elevation={isElevated ? 4 : 0}
        position="static"
        classes={{ root: classes.tabBarRoot }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={tA11y('aria.tabs')}
          classes={{
            root: classes.tabsRoot,
            indicator: classes.tabsIndicator,
          }}
          centered
          selectionFollowsFocus
          variant={'fullWidth'}
        >
          {tabs.map(({ Component, label, Icon }, idx) => {
            const isSelected = idx === value;

            return (
              <Tab
                tabIndex={-1}
                key={`tab_view_${idx}`}
                classes={{
                  root: classes.tabRoot,
                  selected: classes.tabSelected,
                  wrapped: classes.tabWrapper,
                }}
                icon={Icon && <Icon fill={idx === value ? '#2878C1' : '#6A6A73'} />}
                label={
                  Component ? (
                    <Component handleOnClick={handleOnClick} isSelected={isSelected} />
                  ) : (
                    label
                  )
                }
                {...{
                  id: `${tA11y('id.tab')}-${idx}`,
                  'aria-controls': `${tA11y('aria.tabPanel')}-${idx}`,
                }}
                disableFocusRipple
                disableRipple
              />
            );
          })}
        </Tabs>
      </AppBar>
      {tabViews.map(({ Component }, idx) => {
        return (
          <TabView key={`tab_view_${idx}`} value={value} id={idx}>
            <Component singleLetter={singleLetter} fromRoute={location?.state?.from} />
          </TabView>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles((theme: any) => {
  return {
    tabBarRoot: {
      backgroundColor: `${theme.palette.background.paper} !important`,
      position: 'relative',
      zIndex: 1,
    },
    tabsRoot: {
      padding: '0 4rem',
    },
    tabsIndicator: {
      backgroundColor: theme.palette.black.main,
      height: '4px',
      maxWidth: ({ tabsLength }: any) => `${100 / tabsLength}%`,
    },
    tabRoot: {
      ...theme.typography.body1,
      color: theme.palette.black.main,
      maxWidth: 'none',
      padding: '0px',
      textTransform: 'capitalize',
      fontWeight: 'bold',
    },
    tabSelected: {
      color: theme.palette.link.main,
    },
    tabWrapper: {
      flexDirection: 'row',
      '& svg': {
        height: '1.5rem',
        marginRight: '1rem',
        width: 'auto',
      },
    },
  };
});

export default TabBar;
